/**
 * Single source of truth for platform access and subscription tier checks.
 * Keep in sync with RequireAuthAndEarlyAccess gate logic and subscriptionHelpers.js.
 */

/** Local no-card trial past trialEnd (server may still show trialing until next settings refresh). */
export function isLocalNoCardTrialExpired(user) {
  const sub = user?.subscriptionStatus?.subscription;
  if (!sub || sub.status !== 'trialing' || sub.hasStripeBilling) return false;
  if (!sub.trialEnd) return false;
  const trialEnd = new Date(sub.trialEnd);
  return !Number.isNaN(trialEnd.getTime()) && trialEnd <= new Date();
}

/** Any active/trialing subscription grants platform access. */
export function hasTradePlatformAccess(user) {
  if (!user?.token) return false;
  if (isLocalNoCardTrialExpired(user)) return false;
  return (
    user.subscriptionStatus?.hasAccess === true ||
    user.is_demo === true ||
    user.superuser === true
  );
}

/** Canonical plan names. `essentials` and `pro` still resolve for older payloads. */
export function normalizePlanTier(tier) {
  if (tier === 'track' || tier === 'essentials') return 'track';
  if (tier === 'trade' || tier === 'pro') return 'trade';
  return null;
}

/** Returns 'trade' | 'track'. Defaults to 'trade' for older accounts with no plan yet. */
export function getPlanTier(user) {
  if (user?.superuser || user?.is_demo) return 'trade';
  return normalizePlanTier(user?.subscriptionStatus?.planTier) || 'trade';
}

export function isTradeTier(user) {
  return getPlanTier(user) === 'trade';
}

export function isTrackTier(user) {
  return getPlanTier(user) === 'track';
}

export function isProTier(user) {
  return isTradeTier(user);
}

export function isEssentialsTier(user) {
  return isTrackTier(user);
}

/**
 * True when the user's trial or subscription has ended and they need to subscribe.
 * Used to show the TrialExpiredModal overlay.
 */
export function isTrialExpired(user) {
  if (!user?.token) return false;
  if (user.superuser || user.is_demo) return false;
  if (isLocalNoCardTrialExpired(user)) return true;
  const hasAccess = user.subscriptionStatus?.hasAccess === true;
  if (hasAccess) return false;
  const hasUsedTrial = user.subscriptionStatus?.hasUsedTrial === true;
  const status = user.subscriptionStatus?.subscription?.status;
  return hasUsedTrial || status === 'past_due' || status === 'unpaid';
}

/**
 * Trade-plan features: order entry, brackets, and trade-button enforcement.
 * Returns false for Track users even when they have platform access.
 */
export function hasTradeFeatureAccess(user) {
  if (!hasTradePlatformAccess(user)) return false;
  if (user?.superuser || user?.is_demo) return true;
  return isTradeTier(user);
}
